import axios from 'axios';
import { uploadApi } from '../utils/api';
import { buildAPIURL } from '../config/apiConfig';
import { getAuthToken } from '../utils/auth';
import type { 
  ExamGenerationRequest, 
  DraftExamsResponse, 
  SelectOfficialExamRequest, 
  SaveOfficialExamRequest,
  ExamHistoryQuery,
  ExamHistory,
  ExamDetail,
  ExamStatistics,
  ExamStatsQuery,
  GeneratedExam
} from '../../../shared/types/database';

class GenerateExamService {
  private baseUrl = buildAPIURL('/generate-exams');

  // Debug method to check URL
  getBaseUrl(): string {
    return this.baseUrl;
  }

  // Helper method to get authorization header
  private getAuthHeader() {
    // Thử cả 2 key token có thể có
    const token = getAuthToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  // Tạo 10 đề nháp
  async generateDraftExams(request: ExamGenerationRequest): Promise<DraftExamsResponse> {
    const response = await axios.post(`${this.baseUrl}/draft-exams`, request, {
      headers: this.getAuthHeader()
    });
    return response.data;
  }

  // Chọn đề chính thức
  async selectOfficialExam(request: SelectOfficialExamRequest): Promise<{ message: string; selectedExam: any }> {
    const response = await axios.post(`${this.baseUrl}/select-official-exam`, request, {
      headers: this.getAuthHeader()
    });
    return response.data;
  }

  // Lưu đề thi chính thức
  async saveOfficialExam(request: SaveOfficialExamRequest): Promise<{ message: string; deThiId: number; deThiInfo: any }> {
    const response = await axios.post(`${this.baseUrl}/save-official-exam`, request, {
      headers: this.getAuthHeader()
    });
    return response.data;
  }

  // Tải file Word đề thi
  async downloadWordFile(deThiId: number): Promise<void> {
    const response = await axios.get(`${this.baseUrl}/download-word/${deThiId}`, {
      responseType: 'blob',
      headers: this.getAuthHeader()
    });

    // Tạo URL tạm thời cho blob
    const url = window.URL.createObjectURL(new Blob([response.data]));
    
    // Tạo thẻ a ẩn để kích hoạt tải xuống
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `de-thi-${deThiId}.docx`);
    document.body.appendChild(link);
    link.click();
    
    // Dọn dẹp
    window.URL.revokeObjectURL(url);
    document.body.removeChild(link);
  }

  // Test backend connection
  async testConnection(): Promise<any> {
    try {
      
      // Test với exam history endpoint thay vì /health
      const response = await axios.get(`${this.baseUrl}/history`, {
        params: { page: 1, limit: 1 }, // Test với tham số nhỏ
        headers: this.getAuthHeader(),
        timeout: 5000
      });
      return { status: 'OK', message: 'Backend accessible', code: response.status };
    } catch (error: any) {
      throw error;
    }
  }

  // Test upload endpoint
  async testUpload(file: File): Promise<any> {
    if (!file || !(file instanceof File)) {
      throw new Error('File không hợp lệ: ' + typeof file);
    }
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await uploadApi.post(`/generate-exams/test-upload`, formData, {
        timeout: 10000, // 10 seconds timeout
      });
      
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  // Upload file đáp án
  async uploadAnswerFile(deThiId: number, file: File): Promise<{ message: string; fileName: string; originalName: string }> {
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await uploadApi.post(`/generate-exams/upload-answer/${deThiId}`, formData);
      
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Tải file đáp án
  async downloadAnswerFile(deThiId: number): Promise<void> {
    const token = getAuthToken();
    const url = `${this.baseUrl}/download-answer/${deThiId}`;
    
    // Sử dụng window.open để tải file với token
    if (token) {
      // Tạo form tạm thời để gửi token qua POST
      const form = document.createElement('form');
      form.method = 'GET';
      form.action = url;
      form.style.display = 'none';
      
      const tokenInput = document.createElement('input');
      tokenInput.type = 'hidden';
      tokenInput.name = 'Authorization';
      tokenInput.value = `Bearer ${token}`;
      form.appendChild(tokenInput);
      
      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    } else {
      window.open(url, '_blank');
    }
  }

  // Lấy lịch sử đề thi
  async getExamHistory(query?: ExamHistoryQuery): Promise<{ data: ExamHistory[] }> {
    const response = await axios.get(`${this.baseUrl}/history`, {
      params: query,
      headers: this.getAuthHeader()
    });
    
    // Backend trả về array trực tiếp, cần wrap trong { data: [...] }
    const data = Array.isArray(response.data) ? response.data : [];
    return { data };
  }

  // Lấy thống kê đề thi
  async getExamStatistics(query: ExamStatsQuery): Promise<ExamStatistics> {
    const response = await axios.get(`${this.baseUrl}/statistics`, {
      params: query,
      headers: this.getAuthHeader()
    });
    // Handle new response format
    return response.data.data || response.data;
  }

  // Lấy chi tiết đề thi
  async getExamDetail(deThiId: number): Promise<GeneratedExam> {
    const response = await axios.get(`${this.baseUrl}/detail/${deThiId}`, {
      headers: this.getAuthHeader()
    });
    return response.data;
  }
}

const generateExamService = new GenerateExamService();

export default generateExamService;
export { generateExamService };
